import { Link } from "react-router-dom";
import useSEO from "@/lib/useSEO";
import { Card } from "@/components/ui/card";
import { Handshake, MousePointerClick, Link2, Webhook, Mail, CheckCircle2, Ban, TicketPercent } from "lucide-react";

const CodeBlock = ({ children }) => (
  <pre className="mt-3 rounded-xl bg-slate-950 text-slate-100 text-[12px] leading-relaxed p-4 overflow-x-auto font-mono">
    {children}
  </pre>
);

const FIELDS = [
  ["order_id", "string", "Oui", "Identifiant unique de la commande côté tubs.fr (ex. numéro de commande)"],
  ["order_total_eur", "number", "Oui", "Montant total TTC de la commande en euros"],
  ["commission_eur", "number", "Non", "Commission calculée par vos soins. Si absent : montant × taux convenu"],
  ["currency", "string", "Non", "Devise (défaut : EUR)"],
  ["ref", "string", "Non", "Référence d'affiliation reçue (défaut : doctospa)"],
  ["status", "string", "Non", "confirmed (défaut) ou cancelled (annulation / remboursement)"],
  ["occurred_at", "string ISO 8601", "Non", "Date/heure de la commande (défaut : réception du postback)"],
];

export default function PartnerTubs() {
  useSEO({
    title: "Partenariat d'affiliation — Documentation technique | DoctoSpa",
    description: "Documentation d'intégration du suivi des conversions d'affiliation DoctoSpa pour les partenaires e-commerce.",
  });

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-14 lg:py-20" data-testid="partner-tubs-page">
      <div className="eyebrow text-secondary flex items-center gap-2">
        <Handshake className="h-3.5 w-3.5" /> Partenaires
      </div>
      <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
        Suivi des conversions <span className="text-primary">DoctoSpa × tubs.fr</span>
      </h1>
      <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">
        DoctoSpa envoie chaque mois des centaines de visiteurs qualifiés vers tubs.fr : propriétaires de spa
        ayant identifié une panne précise et cherchant la pièce exacte pour leur modèle.
        Cette page documente les 3 façons d'attribuer les commandes issues de ce trafic.
      </p>

      {/* 1 — Identification du trafic */}
      <Card className="mt-10 p-7 border-black/10" data-testid="partner-section-ref">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center"><MousePointerClick className="h-5 w-5" /></span>
          <h2 className="font-display text-xl font-bold">1. Identifier notre trafic — déjà en place</h2>
        </div>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          Tous les liens sortants de DoctoSpa vers tubs.fr contiennent le paramètre{" "}
          <span className="inline-flex items-center rounded-md bg-secondary text-secondary-foreground px-2 py-0.5 text-xs font-mono font-semibold">ref=doctospa</span>.
          Exemple d'URL générée par nos fiches modèles et rapports de diagnostic :
        </p>
        <CodeBlock>{`https://www.tubs.fr/fr/advanced_search_result.php?q=pressostat+balboa&ref=doctospa`}</CodeBlock>
        <p className="mt-3 text-sm text-muted-foreground">
          Il vous suffit de stocker ce paramètre en session/cookie à l'arrivée du visiteur pour attribuer
          sa commande à DoctoSpa (fenêtre d'attribution suggérée : 30 jours).
        </p>
      </Card>

      {/* 2 — Postback */}
      <Card className="mt-6 p-7 border-primary/25 bg-primary/5" data-testid="partner-section-postback">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-primary text-white grid place-items-center"><Webhook className="h-5 w-5" /></span>
          <h2 className="font-display text-xl font-bold">2. Postback automatique — recommandé</h2>
        </div>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          À chaque commande attribuée à <code className="font-mono text-xs bg-black/5 px-1.5 py-0.5 rounded">ref=doctospa</code>,
          votre serveur appelle notre endpoint. Une seule requête HTTP — aucune librairie requise.
        </p>

        <h3 className="mt-5 font-semibold text-sm">Endpoint</h3>
        <CodeBlock>{`POST https://doctospa.fr/api/affiliate/postback
Header : X-Partner-Key: <clé secrète fournie séparément>
Content-Type : application/json`}</CodeBlock>

        <h3 className="mt-5 font-semibold text-sm">Champs du payload JSON</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-2 pr-3">Champ</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Requis</th>
                <th className="text-left py-2 pl-3">Description</th>
              </tr>
            </thead>
            <tbody>
              {FIELDS.map(([f, t, req, desc]) => (
                <tr key={f} className="border-b border-black/5">
                  <td className="py-2 pr-3 font-mono font-semibold">{f}</td>
                  <td className="py-2 px-3 text-muted-foreground">{t}</td>
                  <td className="py-2 px-3">{req}</td>
                  <td className="py-2 pl-3 text-muted-foreground">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="mt-5 font-semibold text-sm">Exemple cURL</h3>
        <CodeBlock>{`curl -X POST https://doctospa.fr/api/affiliate/postback \\
  -H "X-Partner-Key: VOTRE_CLE_SECRETE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "order_id": "TUBS-2026-08451",
    "order_total_eur": 189.90,
    "commission_eur": 9.50,
    "ref": "doctospa"
  }'`}</CodeBlock>

        <h3 className="mt-5 font-semibold text-sm">Exemple PHP (hook post-commande)</h3>
        <CodeBlock>{`if (($_SESSION['affiliate_ref'] ?? '') === 'doctospa') {
    $ch = curl_init('https://doctospa.fr/api/affiliate/postback');
    curl_setopt_array($ch, [
        CURLOPT_POST => true,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER => [
            'X-Partner-Key: VOTRE_CLE_SECRETE',
            'Content-Type: application/json',
        ],
        CURLOPT_POSTFIELDS => json_encode([
            'order_id'        => $order->reference,
            'order_total_eur' => (float) $order->total_paid,
        ]),
    ]);
    curl_exec($ch);
    curl_close($ch);
}`}</CodeBlock>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-black/5">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <span><b>Idempotent</b> — renvoyer le même <code className="font-mono text-xs">order_id</code> met simplement la conversion à jour, aucun doublon possible.</span>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-black/5">
            <Ban className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <span><b>Annulation</b> — en cas de remboursement, renvoyez le même <code className="font-mono text-xs">order_id</code> avec <code className="font-mono text-xs">"status": "cancelled"</code>.</span>
          </div>
        </div>
      </Card>

      {/* 3 — Alternatives */}
      <Card className="mt-6 p-7 border-black/10" data-testid="partner-section-alternatives">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 grid place-items-center"><TicketPercent className="h-5 w-5" /></span>
          <h2 className="font-display text-xl font-bold">3. Alternatives sans développement</h2>
        </div>
        <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <Link2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span><b className="text-foreground">Code promo dédié</b> — créez un code type <code className="font-mono text-xs bg-black/5 px-1.5 py-0.5 rounded">DOCTOSPA5</code> (-5 %) : chaque commande l'utilisant est attribuée à DoctoSpa, sans aucune intégration.</span>
          </li>
          <li className="flex items-start gap-2">
            <Mail className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span><b className="text-foreground">Rapport mensuel</b> — un simple export (nombre de commandes + CA <code className="font-mono text-xs">ref=doctospa</code>) envoyé chaque début de mois à <a href="mailto:contact@doctospa.fr" className="text-primary underline">contact@doctospa.fr</a>.</span>
          </li>
        </ul>
      </Card>

      <Card className="mt-6 p-7 bg-slate-950 text-white border-0" data-testid="partner-contact">
        <h2 className="font-display text-xl font-bold">Contact</h2>
        <p className="mt-2 text-sm text-slate-300 leading-relaxed">
          Questions techniques ou commerciales, demande de clé secrète, taux de commission :
          <a href="mailto:contact@doctospa.fr" className="text-amber-300 underline ml-1">contact@doctospa.fr</a>
        </p>
        <p className="mt-3 text-xs text-slate-400">
          DoctoSpa — Le diagnostic technique de votre spa en 2 minutes · <Link to="/" className="underline">doctospa.fr</Link>
        </p>
      </Card>
    </div>
  );
}
